import { useMemo } from 'react';
import { IRelativePerformance } from '@nkr/models/index';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, FirstDataRenderedEvent, GridReadyEvent, ICellRendererParams, IHeaderParams, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { Label } from 'react-bootstrap';
import { ReportWrapper } from './styles';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';

interface IProps {
    data: IRelativePerformance[],
    onGridReady: (e: GridReadyEvent) => void,
    onFirstDataRendered: (e: FirstDataRenderedEvent) => void
}

const Grid = (props: IProps) => {
    const renderRank = (category: string, value: number) => {
        if (category == 'LV') {
            return (
                <Label bsStyle='warning'>LV</Label>
            );
        }

        if (category == 'NV') {
            return (
                <Label bsStyle='danger'>NV</Label>
            );
        }

        if (category == 'N/A') {
            return (
                <Label bsStyle='danger'>N/A</Label>
            );
        }

        return <div>{value}</div>;
    };

    const getRank = (params: ValueGetterParams, category: string, rank: number) => {
        let s = params.api.getSortModel().find(x => x.colId == params.column.getColId());

        if (s != null) {
            if (s.sort == 'asc' && category == 'NV') {
                return 150;
            }

            if (s.sort == 'desc' && category == 'NV') {
                return -1;
            }
        }

        return rank;
    };

    const renderValue = (category: string, value: any) => {
        if (category == 'LV') {
            return (
                <Label bsStyle='warning'>LV</Label>
            );
        }

        if (category == 'NV') {
            return (
                <Label bsStyle='danger'>NV</Label>
            );
        }

        if (category == 'N/A') {
            return (
                <Label bsStyle='danger'>N/A</Label>
            );
        }

        return <div>{value}</div>;
    };

    const getValue = (params: ValueGetterParams, category: string, value: any) => {
        let s = params.api.getSortModel().find(x => x.colId == params.column.getColId());

        if (s != null) {
            if (s.sort == 'asc' && category == 'NV') {
                return 150;
            }

            if (s.sort == 'desc' && category == 'NV') {
                return -1;
            }
        }

        return value;
    };

    const columns: ColDef[] = [
        {
            colId: 'rowNumber',
            headerName: 'R#',
            width: 55,
            sortable: false,
            pinned: 'left',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1,
        },
        {
            colId: 'overallRank',
            headerName: 'Overall Network Rank',
            width: 75,
            pinned: 'left',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Overall<br />Network<br />Rank</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRelativePerformance;
                return <>{data.overallPerformance.rank}</>
            },
            valueGetter: (params: ValueGetterParams) => parseInt(params.data.overallPerformance.rank),
        },
        {
            colId: 'centerAlias',
            field: 'centerAlias',
            headerName: 'Center',
            width: 125,
            pinned: 'left',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Center</SortableHeaderComponent>,
        },

        {
            colId: 'rankSum',
            headerName: 'Sum',
            width: 55,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Rank<br />Sum</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IRelativePerformance;
                return <>{data.overallPerformance.rankSum}</>
            },
            valueGetter: (params: ValueGetterParams) => parseInt(params.data.overallPerformance.rankSum),
        },

        {
            colId: 'compositeScoreRank',
            field: 'compositeScoreRank',
            headerName: 'Composite Score Rank',
            width: 85,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Composite<br />Score<br />Rank</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderRank(data.compositeScore.category, data.compositeScore.rank);
            },
            valueGetter: (params: ValueGetterParams) => getRank(params, params.data.compositeScore.category, params.data.compositeScore.rank)
        },
        {
            colId: 'compositeScore',
            headerName: 'Composite Score',
            width: 85,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Composite<br />Score</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderValue(data.compositeScore.category, data.compositeScore.compositeScore);
            },
            valueGetter: (params: ValueGetterParams) => getValue(params, params.data.compositeScore.category, params.data.compositeScore.compositeScore),
            sortingOrder: ['desc']
        },

        {
            colId: 'preselectsAverageReviewedRank',
            field: 'preselectsAverageReviewedRank',
            headerName: '30 Day Preselect % Rank',
            width: 85,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>30 Day<br />Preselect<br />% Rank</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderRank(data.preselectsAveragePercentReviewedThisMonth.category, data.preselectsAveragePercentReviewedThisMonth.rank);
            },
            valueGetter: (params: ValueGetterParams) => getRank(params, params.data.preselectsAveragePercentReviewedThisMonth.category, params.data.preselectsAveragePercentReviewedThisMonth.rank)
        },
        {
            colId: 'preselectsAverageReviewed',
            headerName: '30 Day Preselect %',
            width: 85,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>30 Day<br />Preselect<br />%</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderValue(data.preselectsAveragePercentReviewedThisMonth.category, data.preselectsAveragePercentReviewedThisMonth.average + '%');
            },
            valueGetter: (params: ValueGetterParams) => getValue(params, params.data.preselectsAveragePercentReviewedThisMonth.category, params.data.preselectsAveragePercentReviewedThisMonth.average),
            sortingOrder: ['desc']
        },

        {
            colId: 'cprScoreRank',
            field: 'cprScoreRank',
            headerName: 'Compatible Pair Ratio Rank',
            width: 85,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Compatible<br />Pair<br />Ratio Rank</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderRank(data.cprScore.category, data.cprScore.rank);
            },
            valueGetter: (params: ValueGetterParams) => getRank(params, params.data.cprScore.category, params.data.cprScore.rank)
        },
        {
            colId: 'cprScore',
            field: 'cprScore',
            width: 85,
            headerName: 'Compatible Pair Ratio',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Compatible<br />Pair<br />Ratio</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderValue(data.cprScore.category, (data.cprScore.cprScore * 100.0).toFixed(1) + '%');
            },
            valueGetter: (params: ValueGetterParams) => getValue(params, params.data.cprScore.category, params.data.cprScore.cprScore),
            sortingOrder: ['desc']
        },

        {
            colId: 'surgeryDateDeclinesRank',
            field: 'surgeryDateDeclinesRank',
            headerName: 'Surgery Date Decline Rank',
            width: 95,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Surgery<br />Date<br />Decline Rank</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderRank(data.surgeryDateDecline.category, data.surgeryDateDecline.rank);
            },
            valueGetter: (params: ValueGetterParams) => getRank(params, params.data.surgeryDateDecline.category, params.data.surgeryDateDecline.rank)
        },
        {
            colId: 'surgeryDateDeclines',
            field: 'surgeryDateDeclines',
            headerName: 'Surgery Date Decline',
            width: 75,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Surgery<br />Date<br />Decline</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderValue(data.surgeryDateDecline.category, Math.round(data.surgeryDateDecline.declineRate).toFixed(0) + '%');
            },
            valueGetter: (params: ValueGetterParams) => getValue(params, params.data.surgeryDateDecline.category, params.data.surgeryDateDecline.declineRate),
            sortingOrder: ['asc']
        },

        {
            colId: 'swapFailureAnalysisRank',
            field: 'swapFailureAnalysisRank',
            headerName: 'Swap Failure Rate Rank',
            width: 85,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Swap<br />Failure<br />Rate Rank</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderRank(data.swapFailureRate.category, data.swapFailureRate.rank);
            },
            valueGetter: (params: ValueGetterParams) => getRank(params, params.data.swapFailureRate.category, params.data.swapFailureRate.rank)
        },
        {
            colId: 'swapFailureAnalysis',
            field: 'swapFailureAnalysis',
            headerName: 'Swap Failure Rate',
            width: 65,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Swap<br />Failure<br />Rate</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderValue(data.swapFailureRate.category, Math.round(data.swapFailureRate.failureRate).toFixed(0) + '%');
            },
            valueGetter: (params: ValueGetterParams) => getValue(params, params.data.swapFailureRate.category, params.data.swapFailureRate.failureRate),
            sortingOrder: ['asc']
        },

        {
            colId: 'chainEndDeclineRatioRank',
            field: 'chainEndDeclineRatioRank',
            headerName: 'Waitlist Offer Decline Ratio Rank',
            width: 95,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Waitlist Offer<br />Decline<br />Ratio Rank</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderRank(data.chainEndDeclineRatio.category, data.chainEndDeclineRatio.rank);
            },
            valueGetter: (params: ValueGetterParams) => getRank(params, params.data.chainEndDeclineRatio.category, params.data.chainEndDeclineRatio.rank)
        },
        {
            colId: 'chainEndDeclineRatio',
            field: 'chainEndDeclineRatio',
            headerName: 'Waitlist Offer Decline Ratio',
            width: 95,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Waitlist Offer<br />Decline<br />Ratio</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderValue(data.chainEndDeclineRatio.category, data.chainEndDeclineRatio.declineRatio.toFixed(0) + '%');
            },
            valueGetter: (params: ValueGetterParams) => getRank(params, params.data.chainEndDeclineRatio.category, params.data.chainEndDeclineRatio.declineRatio),
            sortingOrder: ['asc']
        },

        {
            colId: 'nkrDonorReferralMedianContactTimeRank',
            field: 'nkrDonorReferralMedianContactTimeRank',
            headerName: 'NKR Donor Referral Median Contact Time Rank',
            width: 125,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>NKR Donor Referral<br />Median Contact Time<br />Rank</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderRank(data.nkrDonorReferralMedianContactTime.category, data.nkrDonorReferralMedianContactTime.rank);
            },
            valueGetter: (params: ValueGetterParams) => getRank(params, params.data.nkrDonorReferralMedianContactTime.category, params.data.nkrDonorReferralMedianContactTime.rank)
        },
        {
            colId: 'nkrDonorReferralMedianContactTime',
            field: 'nkrDonorReferralMedianContactTime',
            headerName: 'NKR Donor Referral Median Contact Time',
            width: 125,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>NKR Donor Referral<br />Median Contact Time<br />&nbsp;</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;

                let value = '';

                if (data.nkrDonorReferralMedianContactTime.contactTimeDaysMedian > 1) {
                    value = data.nkrDonorReferralMedianContactTime.contactTimeDaysMedian + ' days';
                }

                if (data.nkrDonorReferralMedianContactTime.contactTimeDaysMedian == 1) {
                    value = '1 day';
                }

                if (data.nkrDonorReferralMedianContactTime.contactTimeDaysMedian < 1) {
                    value = '< day';
                }

                return renderValue(data.nkrDonorReferralMedianContactTime.category, value);
            },
            valueGetter: (params: ValueGetterParams) => getValue(params, params.data.nkrDonorReferralMedianContactTime.category, params.data.nkrDonorReferralMedianContactTime.contactTimeDaysMedian),
            sortingOrder: ['asc']
        },

        {
            colId: 'nkrDonorReferralConversionRateRank',
            field: 'nkrDonorReferralConversionRateRank',
            headerName: 'NKR Donor Referral Conversion Rate Rank',
            width: 125,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>NKR Donor Referral<br />Conversion Rate<br />Rank</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderRank(data.nkrDonorReferralConversionRate.category, data.nkrDonorReferralConversionRate.rank);
            },
            valueGetter: (params: ValueGetterParams) => getRank(params, params.data.nkrDonorReferralConversionRate.category, params.data.nkrDonorReferralConversionRate.rank)
        },
        {
            colId: 'nkrDonorReferralConversionRate',
            field: 'nkrDonorReferralConversionRate',
            headerName: 'NKR Donor Referral Conversion Rate',
            width: 125,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>NKR Donor Referral<br />Conversion<br />Rate</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;

                let value = '';

                if (data.nkrDonorReferralConversionRate.conversionRate >= 0) {
                    value = data.nkrDonorReferralConversionRate.conversionRate.toFixed(0) + '%';
                }

                if (data.nkrDonorReferralConversionRate.conversionRate < 0) {
                    value = data.nkrDonorReferralConversionRate.conversionRate.toString();
                }

                return renderValue(data.nkrDonorReferralConversionRate.category, value);
            },
            valueGetter: (params: ValueGetterParams) => getValue(params, params.data.nkrDonorReferralConversionRate.category, params.data.nkrDonorReferralConversionRate.conversionRate),
            sortingOrder: ['desc']
        },

        {
            colId: 'svDonationsRank',
            field: 'svDonationsRank',
            headerName: 'Standard Voucher Donations Rank',
            width: 105,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Standard<br />Voucher<br />Donations Rank</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderRank(data.svDonations.category, data.svDonations.rank);
            },
            valueGetter: (params: ValueGetterParams) => getRank(params, params.data.svDonations.category, params.data.svDonations.rank)
        },
        {
            colId: 'svDonations',
            field: 'svDonations',
            headerName: 'Standard Voucher Donations',
            width: 95,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Standard<br />Voucher<br />Donations</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderValue(data.svDonations.category, data.svDonations.totalStandardVouchers);
            },
            valueGetter: (params: ValueGetterParams) => getValue(params, params.data.svDonations.category, params.data.svDonations.totalStandardVouchers),
            sortingOrder: ['desc']
        },

        {
            colId: 'fvDonationsRank',
            field: 'fvDonationsRank',
            headerName: 'Family Voucher Donations Rank',
            width: 105,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Family<br />Voucher<br />Donations Rank</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderRank(data.fvDonations.category, data.fvDonations.rank);
            },
            valueGetter: (params: ValueGetterParams) => getRank(params, params.data.fvDonations.category, params.data.fvDonations.rank)
        },
        {
            colId: 'fvDonations',
            field: 'fvDonations',
            headerName: 'Family Voucher Donations',
            width: 85,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Family<br />Voucher<br />Donations</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderValue(data.fvDonations.category, data.fvDonations.totalFamilyVouchers);
            },
            valueGetter: (params: ValueGetterParams) => getValue(params, params.data.fvDonations.category, params.data.fvDonations.totalFamilyVouchers),
            sortingOrder: ['desc']
        },

        {
            colId: 'voucherDisconnectRank',
            field: 'voucherDisconnectRank',
            headerName: 'NKR - Voucher Disconnect Rate Rank',
            width: 95,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>(NKR)<br />Voucher<br />Disconnect<br />Rate Rank</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderRank(data.voucherDisconnect.category, data.voucherDisconnect.rank);
            },
            valueGetter: (params: ValueGetterParams) => getRank(params, params.data.voucherDisconnect.category, params.data.voucherDisconnect.rank)
        },
        {
            colId: 'voucherDisconnectRank',
            field: 'voucherDisconnectRank',
            headerName: 'NKR - Voucher Disconnect Rate',
            width: 95,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>(NKR)<br />Voucher<br />Disconnect<br />Rate</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderValue(data.voucherDisconnect.category, Math.round(data.voucherDisconnect.percentDisconnect).toFixed() + '%');
            },
            valueGetter: (params: ValueGetterParams) => getValue(params, params.data.voucherDisconnect.category, params.data.voucherDisconnect.percentDisconnect),
            sortingOrder: ['asc']
        },

        {
            colId: 'privateLabelVoucherDisconnectRank',
            field: 'privateLabelVoucherDisconnectRank',
            headerName: 'PL DASH - Voucher Disconnect Rate Rank',
            width: 95,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>(PL DASH)<br />Voucher<br />Disconnect<br />Rate Rank</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderRank(data.privateLabelVoucherDisconnect.category, data.privateLabelVoucherDisconnect.rank);
            },
            valueGetter: (params: ValueGetterParams) => getRank(params, params.data.privateLabelVoucherDisconnect.category, params.data.privateLabelVoucherDisconnect.rank)
        },
        {
            colId: 'privateLabelVoucherDisconnect',
            field: 'privateLabelVoucherDisconnect',
            headerName: 'PL DASH - Voucher Disconnect Rate',
            width: 95,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>(PL DASH)<br />Voucher<br />Disconnect<br />Rate</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderValue(data.privateLabelVoucherDisconnect.category, Math.round(data.privateLabelVoucherDisconnect.percentDisconnect).toString() + '%');
            },
            valueGetter: (params: ValueGetterParams) => getValue(params, params.data.privateLabelVoucherDisconnect.category, params.data.privateLabelVoucherDisconnect.percentDisconnect),
            sortingOrder: ['asc']
        },

        {
            colId: 'combinedVoucherDisconnectRank',
            field: 'combinedVoucherDisconnectRank',
            headerName: 'NKR and PL DASH - Voucher Disconnect Rate Rank',
            width: 95,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>(NKR/PL DASH)<br />Voucher<br />Disconnect<br />Rate Rank</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderRank(data.combinedVoucherDisconnect.category, data.combinedVoucherDisconnect.rank);
            },
            valueGetter: (params: ValueGetterParams) => getRank(params, params.data.combinedVoucherDisconnect.category, params.data.combinedVoucherDisconnect.rank)
        },
        {
            colId: 'combinedVoucherDisconnect',
            field: 'combinedVoucherDisconnect',
            headerName: 'NKR and PL DASH - Voucher Disconnect Rate',
            width: 95,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>(NKR/PL DASH)<br />Voucher<br />Disconnect<br />Rate</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderValue(data.combinedVoucherDisconnect.category, Math.round(data.combinedVoucherDisconnect.percentDisconnect).toString() + '%');
            },
            valueGetter: (params: ValueGetterParams) => getValue(params, params.data.combinedVoucherDisconnect.category, params.data.combinedVoucherDisconnect.percentDisconnect),
            sortingOrder: ['asc']
        },

        {
            colId: 'missedMatchOfferDeadlineRank',
            field: 'missedMatchOfferDeadlineRank',
            headerName: 'Missed Match Offer Deadline Rank',
            width: 105,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Missed<br />Match Offer<br />Deadline Rank</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderRank(data.missedMatchOfferDeadline.category, data.missedMatchOfferDeadline.rank);
            },
            valueGetter: (params: ValueGetterParams) => getRank(params, params.data.missedMatchOfferDeadline.category, params.data.missedMatchOfferDeadline.rank)
        },
        {
            colId: 'missedMatchOfferDeadline',
            field: 'missedMatchOfferDeadline',
            headerName: 'Missed Match Offer Deadline',
            width: 90,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Missed<br />Match Offer<br />Deadline</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderValue(data.missedMatchOfferDeadline.category, Math.round(data.missedMatchOfferDeadline.ratio).toString() + '%');
            },
            valueGetter: (params: ValueGetterParams) => getValue(params, params.data.missedMatchOfferDeadline.category, params.data.missedMatchOfferDeadline.ratio),
            sortingOrder: ['asc']
        },

        {
            colId: 'kflSuccessRateRank',
            field: 'kflSuccessRateRank',
            headerName: 'KFL Success Rate Rank',
            width: 85,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>KFL<br />Success<br />Rate Rank</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderRank(data.kflSuccessRate.category, data.kflSuccessRate.rank);
            },
            valueGetter: (params: ValueGetterParams) => getRank(params, params.data.kflSuccessRate.category, params.data.kflSuccessRate.rank)
        },
        {
            colId: 'kflSuccessRate',
            field: 'kflSuccessRate',
            headerName: 'KFL Success Rate',
            width: 75,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>KFL<br />Success<br />Rate</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderValue(data.kflSuccessRate.category, Math.round(data.kflSuccessRate.kidneyForLifeSuccessRate).toString() + '%');
            },
            valueGetter: (params: ValueGetterParams) => getValue(params, params.data.kflSuccessRate.category, params.data.kflSuccessRate.kidneyForLifeSuccessRate),
            sortingOrder: ['desc']
        },

        {
            colId: 'micrositePatientReferralSuccessRateRank',
            field: 'micrositePatientReferralSuccessRateRank',
            headerName: 'Microsite Patient Referral Success Rate Rank',
            width: 125,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Microsite Patient<br />Referral<br />Success Rate Rank</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderRank(data.micrositePatientReferralSuccessRate.category, data.micrositePatientReferralSuccessRate.rank);
            },
            valueGetter: (params: ValueGetterParams) => getRank(params, params.data.micrositePatientReferralSuccessRate.category, params.data.micrositePatientReferralSuccessRate.rank)
        },
        {
            colId: 'micrositePatientReferralSuccessRate',
            field: 'micrositePatientReferralSuccessRate',
            headerName: 'Microsite Patient Referral Success Rate',
            width: 115,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Microsite Patient<br />Referral<br />Success Rate</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderValue(data.micrositePatientReferralSuccessRate.category, Math.round(data.micrositePatientReferralSuccessRate.conversionRate).toString() + '%');
            },
            valueGetter: (params: ValueGetterParams) => getValue(params, params.data.micrositePatientReferralSuccessRate.category, params.data.micrositePatientReferralSuccessRate.conversionRate),
            sortingOrder: ['desc']
        },

        {
            colId: 'micrositeEnrollmentSuccessRateRank',
            field: 'micrositeEnrollmentSuccessRateRank',
            headerName: 'Microsite Enrollment Success Rank',
            width: 120,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Microsite<br />Enrollment<br />Success Rate Rank</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderRank(data.micrositeEnrollmentRate.category, data.micrositeEnrollmentRate.rank);
            },
            valueGetter: (params: ValueGetterParams) => getRank(params, params.data.micrositeEnrollmentRate.category, params.data.micrositeEnrollmentRate.rank)
        },
        {
            colId: 'micrositeEnrollmentSuccessRate',
            field: 'micrositeEnrollmentSuccessRate',
            headerName: 'Microsite Enrollment Success',
            width: 95,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Microsite<br />Enrollment<br />Success Rate</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderValue(data.micrositeEnrollmentRate.category, Math.round(data.micrositeEnrollmentRate.micrositeEnrollmentRate).toString() + '%');
            },
            valueGetter: (params: ValueGetterParams) => getRank(params, params.data.micrositeEnrollmentRate.category, params.data.micrositeEnrollmentRate.micrositeEnrollmentRate),
            sortingOrder: ['desc']
        },

        {
            colId: 'fiveStarSurveyResultsRank',
            field: 'fiveStarSurveyResultsRank',
            headerName: 'Donor Satisfaction Surveys Rank',
            width: 95,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Donor<br />Satisfaction<br />Surveys Rank</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderRank(data.fiveStarSurveyResults.category, data.fiveStarSurveyResults.rank);
            },
            valueGetter: (params: ValueGetterParams) => getRank(params, params.data.fiveStarSurveyResults.category, params.data.fiveStarSurveyResults.rank)
        },
        {
            colId: 'fiveStarSurveyResults',
            field: 'fiveStarSurveyResults',
            headerName: 'Donor Satisfaction Surveys',
            width: 85,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Donor<br />Satisfaction<br />Surveys</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IRelativePerformance;
                return renderValue(data.fiveStarSurveyResults.category, data.fiveStarSurveyResults.average.toFixed(1));
            },
            valueGetter: (params: ValueGetterParams) => getRank(params, params.data.fiveStarSurveyResults.category, params.data.fiveStarSurveyResults.average),
            sortingOrder: ['desc']
        },
    ];

    const columnDefs = useMemo(() => columns, []);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <ReportWrapper className='ag-theme-bootstrap'>
            <AgGridReact
                defaultColDef={{
                    headerClass: 'list-header',
                    cellClass: 'list-cell',
                    sortable: true,
                    resizable: false,
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={66}
                groupHeaderHeight={30}
                rowHeight={30}
                suppressRowClickSelection={false}
                suppressCellSelection={true}
                applyColumnDefOrder={true}
                noRowsOverlayComponentFramework={() => <div>No results found...</div>}
                onGridReady={props.onGridReady}
                onFirstDataRendered={props.onFirstDataRendered}
                onSortChanged={(e: SortChangedEvent) => {
                    e.api.refreshCells();
                }}
            />
        </ReportWrapper>
    );
}

export default Grid;